/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    Configuration,
    ConfigurationParameters,
    ContentPreviewGetV3Request,
    PreviewGroup,
    PreviewTagApi,
    RecordingPreviewGetV3Request,
    ScreenType,
} from 'client-code/previews';

import { CommonHeaders } from '../CommonHeaders';
import { Constants } from '../constants';
import { IPreviewsServiceRepository, IRecordingPreviewParams } from '../interfaces';
import { Log } from '../logging';
import { PreviewsServiceV3Contract } from '../servicecontracts';
import {
    TContentPreview,
    TOfferPreview,
    TServicesParams,
    TUnifiedCollectionPreview,
    TUnifiedCollectionPreviewParams,
} from '../types';
import { UniqueIdentifier } from '../utils';

export class PreviewsServiceRepositoryImplV3
    implements IPreviewsServiceRepository<PreviewsServiceV3Contract>
{
    private static TAG: string = 'PreviewsServiceRepositoryImplV3';

    private mParams: TServicesParams;

    public constructor(params: TServicesParams) {
        this.mParams = params;
    }

    private getConfigurationParameters(commonHeaders): ConfigurationParameters {
        return {
            apiKey: this.mParams.accessToken,
            basePath: this.mParams.basePath,
            baseOptions: {
                timeout: Constants.SERVICE_CONNECTION_TIMEOUT,
                headers: commonHeaders,
            },
        };
    }

    private getCommonRequestParams(commonHeaders: CommonHeaders['headers']) {
        return {
            applicationFeatureArea: commonHeaders.ApplicationFeatureArea,
            applicationVersion: commonHeaders.ApplicationVersion,
            bodyId: this.mParams.bodyId,
            timeZoneName: this.mParams.timeZoneName,
            applicationName: commonHeaders.ApplicationName,
            deviceType: commonHeaders.DeviceType,
            productName: commonHeaders.ProductName,
            acceptLanguage: commonHeaders['Accept-Language'],
            xAmznRequestId: UniqueIdentifier.generateXAmznRequestId(),
            whiny: this.mParams.whiny || 'false',
        };
    }

    private async getContentPreviewFromServiceV3(
        contentId: string,
        partnerId?: string,
        collapsedVodItemId?: string,
        groups?: Array<PreviewGroup>,
        contentSupplierPartnerId?: string,
    ): Promise<TContentPreview> {
        try {
            const commonHeaders = new CommonHeaders(ScreenType.SeriesScreenItem).headers;
            const contentPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(contentPreviewConfigParameters);
            const previewsApi = new PreviewTagApi(configuration);
            const contentPreviewV3RequestParameters: ContentPreviewGetV3Request = {
                contentId,
                partnerId,
                collapsedVodItemId,
                groups,
                contentSupplierPartnerId,
                ...this.getCommonRequestParams(commonHeaders),
            };
            const previewsContentResponse = await previewsApi.contentPreviewGetV3(
                contentPreviewV3RequestParameters,
            );

            return previewsContentResponse.data;
        } catch (error: any) {
            Log.e(PreviewsServiceRepositoryImplV3.TAG, 'getContentPreviewFromServiceV3()', error);
            throw error;
        }
    }

    private async getRecordingPreviewFromServiceV3(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        try {
            const commonHeaders = new CommonHeaders(recordingPreviewParams.applicationFeatureArea)
                .headers;
            const recordingPreviewConfigParameters = this.getConfigurationParameters(commonHeaders);
            const configuration = new Configuration(recordingPreviewConfigParameters);
            const previewsApi = new PreviewTagApi(configuration);
            const recordingPreviewV3RequestParameters: RecordingPreviewGetV3Request = {
                ...this.getCommonRequestParams(commonHeaders),
                recordingId: recordingPreviewParams.recordingId,
                channelNumber: recordingPreviewParams.channelNumber,
                groups: recordingPreviewParams.groups,
            };
            const previewsRecordingResponse = await previewsApi.recordingPreviewGetV3(
                recordingPreviewV3RequestParameters,
            );

            const recordingPreviewResponse: TOfferPreview = {
                offerPreview: previewsRecordingResponse.data || {},
                networkCallId: recordingPreviewParams.networkCallId,
            };

            return recordingPreviewResponse;
        } catch (error: any) {
            error.networkCallId = recordingPreviewParams.networkCallId;
            Log.e(PreviewsServiceRepositoryImplV3.TAG, 'getRecordingPreviewFromServiceV3()', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchContentPreview(params: {
        contentId: string;
        partnerId?: string;
        collapsedVodItemId?: string;
        groups?: Array<PreviewGroup>;
        contentSupplierPartnerId?: string;
    }): Promise<TContentPreview> {
        try {
            const { contentId, partnerId, collapsedVodItemId, contentSupplierPartnerId, groups } =
                params;
            return await this.getContentPreviewFromServiceV3(
                contentId,
                partnerId,
                collapsedVodItemId,
                groups,
                contentSupplierPartnerId,
            );
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImplV3.TAG, 'fetchContentPreview failed', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public async fetchOfferPreview(
        offerId: string,
        screenType: ScreenType,
        networkCallId?: number,
    ): Promise<TOfferPreview> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchRecordingPreview(
        recordingPreviewParams: IRecordingPreviewParams,
    ): Promise<TOfferPreview> {
        try {
            return await this.getRecordingPreviewFromServiceV3(recordingPreviewParams);
        } catch (error) {
            Log.e(PreviewsServiceRepositoryImplV3.TAG, 'error', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    public fetchUnifiedCollectionPreview(
        unifiedCollectionPreviewParams: TUnifiedCollectionPreviewParams,
    ): Promise<TUnifiedCollectionPreview> {
        throw new Error('Method not implemented.');
    }

    /**
     * @inheritdoc
     */
    public async fetchVodOfferPreview(params: {
        vodOfferId: string;
        screenType: ScreenType;
        networkCallId?;
    }): Promise<TOfferPreview> {
        throw new Error('Method not implemented.');
    }
}
